import { z } from 'zod';
import { SendAsIdentity } from './compose-tools.js';

export const TlsMode = z.enum(['tls', 'starttls', 'none']);
export type TlsMode = z.infer<typeof TlsMode>;

export const AccountStatus = z.enum([
  'idle',
  'connected',
  'syncing',
  'backoff',
  'auth_failed',
  'error',
]);
export type AccountStatus = z.infer<typeof AccountStatus>;

export const MAIL_REFRESH_INTERVAL_OPTIONS = [1, 5, 15, 30, 60] as const;
export const MailRefreshIntervalMinutes = z
  .union([z.literal(1), z.literal(5), z.literal(15), z.literal(30), z.literal(60)])
  .nullable();
export type MailRefreshIntervalMinutes = z.infer<typeof MailRefreshIntervalMinutes>;

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);
export const MAX_ACCOUNT_SIGNATURE_HTML_LENGTH = 100_000;

/** Connection settings without secrets — the shape the API returns. */
export const Account = z.object({
  id: z.number().int(),
  name: z.string(),
  email: z.string(),
  color: hexColor,
  fromName: z.string(),
  signatureHtml: z.string().max(MAX_ACCOUNT_SIGNATURE_HTML_LENGTH),
  sendAsIdentities: z.array(SendAsIdentity).max(20).optional(),
  imapHost: z.string(),
  imapPort: z.number().int(),
  imapTls: TlsMode,
  imapUser: z.string(),
  smtpHost: z.string(),
  smtpPort: z.number().int(),
  smtpTls: TlsMode,
  smtpUser: z.string(),
  refreshIntervalMinutes: MailRefreshIntervalMinutes,
  allowPrivateNetwork: z.boolean(),
  enabled: z.boolean(),
  status: AccountStatus,
  statusError: z.string().nullable(),
  createdAt: z.number().int(),
});
export type Account = z.infer<typeof Account>;

export const AccountCreate = z.object({
  name: z.string().min(1).max(100),
  email: z.email(),
  color: hexColor,
  fromName: z.string().max(200).default(''),
  signatureHtml: z.string().max(MAX_ACCOUNT_SIGNATURE_HTML_LENGTH).default(''),
  imapHost: z.string().trim().min(1).max(253),
  imapPort: z.number().int().min(1).max(65535).default(993),
  imapTls: TlsMode.default('tls'),
  imapUser: z.string().min(1).max(1024),
  imapPass: z.string().min(1).max(4096),
  smtpHost: z.string().trim().min(1).max(253),
  smtpPort: z.number().int().min(1).max(65535).default(465),
  smtpTls: TlsMode.default('tls'),
  smtpUser: z.string().min(1).max(1024),
  smtpPass: z.string().min(1).max(4096),
  refreshIntervalMinutes: MailRefreshIntervalMinutes.default(null),
  allowPrivateNetwork: z.boolean().default(false),
  /** Required when allowPrivateNetwork is set — private-network reach is a sensitive capability. */
  currentPassword: z.string().min(1).max(1_024).optional(),
});
export type AccountCreate = z.infer<typeof AccountCreate>;

/** Password fields empty/omitted = keep the stored secret. No create-time defaults apply to PATCH. */
export const AccountUpdate = z.object({
  name: z.string().min(1).max(100).optional(),
  email: z.email().optional(),
  color: hexColor.optional(),
  fromName: z.string().max(200).optional(),
  signatureHtml: z.string().max(MAX_ACCOUNT_SIGNATURE_HTML_LENGTH).optional(),
  imapHost: z.string().trim().min(1).max(253).optional(),
  imapPort: z.number().int().min(1).max(65535).optional(),
  imapTls: TlsMode.optional(),
  imapUser: z.string().min(1).max(1024).optional(),
  imapPass: z.string().max(4096).optional(),
  smtpHost: z.string().trim().min(1).max(253).optional(),
  smtpPort: z.number().int().min(1).max(65535).optional(),
  smtpTls: TlsMode.optional(),
  smtpUser: z.string().min(1).max(1024).optional(),
  smtpPass: z.string().max(4096).optional(),
  refreshIntervalMinutes: MailRefreshIntervalMinutes.optional(),
  allowPrivateNetwork: z.boolean().optional(),
  enabled: z.boolean().optional(),
  /** Required when an existing IMAP/SMTP connection or provider password changes. */
  currentPassword: z.string().min(1).max(1_024).optional(),
});
export type AccountUpdate = z.infer<typeof AccountUpdate>;

/** Standalone connection test (pre-save) — full credentials required. */
export const AccountTest = AccountCreate.pick({
  imapHost: true,
  imapPort: true,
  imapTls: true,
  imapUser: true,
  imapPass: true,
  smtpHost: true,
  smtpPort: true,
  smtpTls: true,
  smtpUser: true,
  smtpPass: true,
  allowPrivateNetwork: true,
  currentPassword: true,
});
export type AccountTest = z.infer<typeof AccountTest>;

const probe = z.object({ ok: z.boolean(), error: z.string().nullable() });
export const AccountTestResult = z.object({
  imap: probe,
  smtp: probe,
});
export type AccountTestResult = z.infer<typeof AccountTestResult>;

export const SpecialUse = z.enum([
  'inbox',
  'sent',
  'drafts',
  'trash',
  'junk',
  'archive',
  'all',
  'flagged',
]);
export type SpecialUse = z.infer<typeof SpecialUse>;

export const Folder = z.object({
  id: z.number().int(),
  accountId: z.number().int(),
  path: z.string(),
  name: z.string(),
  delimiter: z.string().nullable(),
  specialUse: SpecialUse.nullable(),
  totalCount: z.number().int(),
  unreadCount: z.number().int(),
  syncEnabled: z.boolean(),
});
export type Folder = z.infer<typeof Folder>;

export const AccountList = z.array(Account);
export const FolderList = z.array(Folder);

const NavigationUnreadCount = z.object({
  id: z.number().int().positive(),
  unread: z.number().int().nonnegative(),
});

export const MailNavigationCounts = z.object({
  unifiedUnread: z.number().int().nonnegative(),
  snoozed: z.number().int().nonnegative().default(0),
  accounts: z.array(NavigationUnreadCount),
  folders: z.array(NavigationUnreadCount),
});
export type MailNavigationCounts = z.infer<typeof MailNavigationCounts>;
